// src/pages/Analytics.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import AppHeader from "../components/AppHeader";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, ComposedChart,
} from "recharts";

const fmtDollar = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

const mergeTrends = (inventory = [], valuation = []) => {
  const map = {};
  inventory.forEach((i) => { map[i.month] = { month: i.month, count: i.count || 0, value: i.value || 0 }; });
  valuation.forEach((v) => {
    if (!map[v.month]) map[v.month] = { month: v.month, count: 0, value: 0 };
    map[v.month].value = v.value || 0;
  });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
};

const sortData = (data = [], { column, direction }) =>
  [...data].sort((a, b) => {
    let x = a[column], y = b[column];
    if (typeof x === "string") x = x.toLowerCase();
    if (typeof y === "string") y = y.toLowerCase();
    if (x < y) return direction === "asc" ? -1 : 1;
    if (x > y) return direction === "asc" ? 1 : -1;
    return 0;
  });

// ── Collapsible section header ────────────────────────────────────────────────
function SectionHeader({ id, label, open, onToggle, count, value }) {
  return (
    <div
      onClick={() => onToggle(id)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        cursor: "pointer", padding: "0.6rem 1rem",
        background: "var(--bg-muted)", borderRadius: 8,
        border: "1px solid var(--border)", marginBottom: open ? "0.75rem" : 0,
        userSelect: "none",
      }}
    >
      <span style={{ fontWeight: 700, fontSize: "1rem" }}>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: "1rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
        {count != null && <span>{count.toLocaleString()} items</span>}
        {value != null && <span style={{ color: "#2e7d32", fontWeight: 600 }}>{fmtDollar(value)}</span>}
        <span style={{ fontSize: "0.9rem" }}>{open ? "▲" : "▼"}</span>
      </span>
    </div>
  );
}

// ── Sortable breakdown table ──────────────────────────────────────────────────
function BreakdownTable({ data = [], labelKey = "label", labelHeader, onLabelClick }) {
  const [sort, setSort] = useState({ column: labelKey, direction: "asc" });
  const toggle = (col) => setSort(prev => ({ column: col, direction: prev.column === col && prev.direction === "asc" ? "desc" : "asc" }));
  const arrow = (col) => sort.column === col ? (sort.direction === "asc" ? " ▲" : " ▼") : "";
  const sorted = sortData(data, sort);

  return (
    <table className="analytics-table" style={{ width: "100%", marginBottom: "0.5rem" }}>
      <thead>
        <tr>
          <th onClick={() => toggle(labelKey)} style={{ cursor: "pointer", textAlign: "left" }}>{labelHeader}{arrow(labelKey)}</th>
          <th onClick={() => toggle("count")} style={{ cursor: "pointer" }}>Count{arrow("count")}</th>
          <th onClick={() => toggle("value")} style={{ cursor: "pointer" }}>Value{arrow("value")}</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row, i) => (
          <tr key={i}>
            <td
              style={onLabelClick ? { cursor: "pointer", color: "#007bff", textDecoration: "underline" } : {}}
              onClick={onLabelClick ? () => onLabelClick(row) : undefined}
            >{row[labelKey]}</td>
            <td>{row.count}</td>
            <td>{fmtDollar(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Small bar chart for a breakdown ──────────────────────────────────────────
function BreakdownBar({ data = [], labelKey = "label", color = "#517fd4" }) {
  if (!data.length) return null;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={labelKey} angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={(v) => fmtDollar(v)} width={70} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => fmtDollar(v)} />
        <Bar dataKey="value" fill={color} name="Value" />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Generic inventory section ─────────────────────────────────────────────────
function InventorySection({ id, label, data, open, onToggle, breakdowns, color }) {
  if (!data) return null;
  return (
    <div style={{ marginBottom: "1rem" }}>
      <SectionHeader id={id} label={label} open={open} onToggle={onToggle} count={data.total} value={data.total_value} />
      {open && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem", padding: "0 0.25rem" }}>
          {breakdowns.map(({ key, header }) => (
            <div key={key}>
              <h4 style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{header}</h4>
              <BreakdownTable data={data[key]} labelKey="label" labelHeader={header} />
              <BreakdownBar data={data[key]} labelKey="label" color={color} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Analytics() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [openSections, setOpenSections] = useState(new Set(["cards"]));
  const [activeTrend, setActiveTrend] = useState("inventory");

  const [showReport, setShowReport]       = useState(false);
  const [reportIncCards, setReportIncCards]   = useState(() => localStorage.getItem("report_inc_cards") !== "false");
  const [reportIncBalls, setReportIncBalls]   = useState(() => localStorage.getItem("report_inc_balls") === "true");
  const [reportIncWax,   setReportIncWax]     = useState(() => localStorage.getItem("report_inc_wax")   === "true");
  const [reportIncPacks, setReportIncPacks]   = useState(() => localStorage.getItem("report_inc_packs") === "true");
  const [reportIncSets,  setReportIncSets]    = useState(() => localStorage.getItem("report_inc_sets")  === "true");
  const [reportSort, setReportSort]       = useState(() => localStorage.getItem("report_sort") || "last_name");
  const [reportScope, setReportScope]     = useState(() => localStorage.getItem("report_scope") || "all");
  const [reportValued, setReportValued]   = useState(() => localStorage.getItem("report_valued") === "true");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError]     = useState(null);

  // Cards breakdown sorts
  const [brandSort, setBrandSort] = useState({ column: "brand", direction: "asc" });
  const [yearSort, setYearSort] = useState({ column: "year", direction: "asc" });
  const [playerSort, setPlayerSort] = useState({ column: "name", direction: "asc" });

  useEffect(() => {
    api.get("/analytics/").then((res) => setStats(res.data)).catch((err) => console.error(err));
  }, []);

  // Kiosk: click analytics rows programmatically
  useEffect(() => {
    const handler = (e) => {
      const { table, value } = e.detail;
      if (table === "brand")  navigate("/list-cards", { state: { brandFilter: value,   limit: "all" } });
      if (table === "year")   navigate("/list-cards", { state: { yearFilter: String(value), limit: "all" } });
      if (table === "player") navigate("/list-cards", { state: { lastNameFilter: value.split(" ").slice(-1)[0], limit: "all" } });
    };
    window.addEventListener("kiosk-analytics-click", handler);
    return () => window.removeEventListener("kiosk-analytics-click", handler);
  }, [navigate]);

  if (!stats) return <p style={{ textAlign: "center", marginTop: "3rem" }}>Loading analytics…</p>;

  const toggleSection = (id) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSort = (setFn, column) => setFn(prev => ({
    column, direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
  }));
  const arrow = (cfg, col) => cfg.column === col ? (cfg.direction === "asc" ? " ▲" : " ▼") : "";

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportError(null);
    localStorage.setItem("report_inc_cards", reportIncCards);
    localStorage.setItem("report_inc_balls", reportIncBalls);
    localStorage.setItem("report_inc_wax",   reportIncWax);
    localStorage.setItem("report_inc_packs", reportIncPacks);
    localStorage.setItem("report_inc_sets",  reportIncSets);
    localStorage.setItem("report_sort",   reportSort);
    localStorage.setItem("report_scope",  reportScope);
    localStorage.setItem("report_valued", reportValued);
    try {
      const res = await api.get("/report/", {
        params: {
          include_cards: reportIncCards,
          include_balls: reportIncBalls,
          include_wax:   reportIncWax,
          include_packs: reportIncPacks,
          include_sets:  reportIncSets,
          cards_sort:        reportSort,
          cards_scope:       reportScope,
          cards_valued_only: reportValued,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cardstoard_report.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      setShowReport(false);
    } catch {
      setReportError("Failed to generate report. Please try again.");
    } finally {
      setReportLoading(false);
    }
  };

  const trendInventory = stats.trend_inventory || [];
  const trendValuation = stats.trend_valuation || [];
  const trendCombined = mergeTrends(trendInventory, trendValuation);

  const cardsOpen = openSections.has("cards");
  const aggTypes = stats.aggregate?.by_type || [];

  return (
    <>
      <AppHeader />
      <div className="container" style={{ maxWidth: "1100px" }}>
        <h2 className="page-header" style={{ textAlign: "center" }}>Collection Analytics</h2>
        <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
          <button
            className="nav-btn"
            onClick={() => { setShowReport(true); setReportError(null); }}
            style={{ background: "#ffc107", color: "#222", border: "1px solid #e0a800", fontSize: "0.85rem", fontWeight: 500 }}
          >
            📑 Generate Report
          </button>
        </div>

        {/* ── Aggregate Overview ─────────────────────────────────────────────── */}
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "1.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <div className="summary-box" style={{ minWidth: 160 }}>
              Total Collection Value<br />
              <strong style={{ fontSize: "1.2rem", color: "#2e7d32" }}>{fmtDollar(stats.aggregate?.total_value)}</strong>
            </div>
            {aggTypes.map(t => (
              <div key={t.type} className="summary-box" style={{ minWidth: 130 }}>
                {t.type}<br />
                <strong>{t.count.toLocaleString()}</strong> · <span style={{ color: "#2e7d32" }}>{fmtDollar(t.value)}</span>
              </div>
            ))}
          </div>
          {aggTypes.length > 1 && (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={aggTypes} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => fmtDollar(v)} width={80} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtDollar(v)} />
                <Bar dataKey="value" fill="#1976d2" name="Value" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Cards Section ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: "1rem" }}>
          <SectionHeader id="cards" label="📇 Cards" open={cardsOpen} onToggle={toggleSection}
            count={stats.total_cards} value={stats.total_value} />

          {cardsOpen && (
            <div>
              {/* Trend charts */}
              <div data-tour="analytics-trend" style={{ margin: "1rem 0" }}>
                <div style={{ marginBottom: "0.75rem", display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                  {[
                    { key: "inventory", label: "📦 Inventory", color: "#517fd4", always: true,  tip: "Cards added to your collection over time" },
                    { key: "valuation", label: "💰 Valuation", color: "#167e30", always: false, tip: "Total collection value trend over time" },
                    { key: "combined",  label: "📊 Combined",  color: "#444",    always: false, tip: "Cards added vs. total value — side by side" },
                  ].filter(opt => opt.always || trendValuation.length > 0).map(opt => (
                    <button key={opt.key} onClick={() => setActiveTrend(opt.key)} data-tip={opt.tip} style={{
                      padding: "0.35rem 0.9rem", borderRadius: 8, border: "none", cursor: "pointer", fontSize: "0.85rem",
                      backgroundColor: activeTrend === opt.key ? opt.color : "#ccc",
                      color: activeTrend === opt.key ? "#fff" : "#000",
                    }}>{opt.label}</button>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  {(() => {
                    if (activeTrend === "inventory" && trendInventory.length > 0)
                      return (
                        <BarChart data={trendInventory} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Legend />
                          <Bar dataKey="count" fill="#517fd4" name="Cards Added" />
                        </BarChart>
                      );
                    if (activeTrend === "valuation" && trendValuation.length > 0)
                      return (
                        <LineChart data={trendValuation} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip formatter={(v) => fmtDollar(v)} /><Legend />
                          <Line type="monotone" dataKey="value" stroke="#167e30" strokeWidth={2} name="Total Value" />
                        </LineChart>
                      );
                    if (activeTrend === "combined" && trendCombined.length > 0)
                      return (
                        <ComposedChart data={trendCombined} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" />
                          <YAxis yAxisId="left" /><YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v.toLocaleString()}`} />
                          <Tooltip formatter={(v, n) => n?.includes("Value") ? fmtDollar(v) : v} /><Legend />
                          <Bar yAxisId="left" dataKey="count" fill="#517fd4" name="Cards Added" />
                          <Line yAxisId="right" type="monotone" dataKey="value" stroke="#167e30" strokeWidth={2} name="Total Value" />
                        </ComposedChart>
                      );
                    return <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 16, fill: "#666" }}>No trend data available</text>;
                  })()}
                </ResponsiveContainer>
              </div>

              {/* Card breakdown tables */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem" }}>
                {/* By Brand */}
                <div>
                  <h4 data-tour="analytics-brand" style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }} data-tip="Card count and total value grouped by manufacturer (Topps, Bowman, Fleer, etc.)">By Brand</h4>

                  <table className="analytics-table" style={{ width: "100%" }}>
                    <thead><tr>
                      <th onClick={() => toggleSort(setBrandSort, "brand")} style={{ cursor: "pointer", textAlign: "left" }}>Brand{arrow(brandSort, "brand")}</th>
                      <th onClick={() => toggleSort(setBrandSort, "count")} style={{ cursor: "pointer" }}>Count{arrow(brandSort, "count")}</th>
                      <th onClick={() => toggleSort(setBrandSort, "value")} style={{ cursor: "pointer" }}>Value{arrow(brandSort, "value")}</th>
                    </tr></thead>
                    <tbody>{sortData(stats.by_brand, brandSort).map(row => (
                      <tr key={row.brand ?? "__unknown__"}>
                        <td style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                          data-tip={`Click to filter Cards by ${row.brand}`}
                          onClick={() => navigate("/list-cards", { state: { brandFilter: row.brand, limit: "all" } })}>{row.brand}</td>
                        <td>{row.count}</td><td>{fmtDollar(row.value)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>

                {/* By Year */}
                <div>
                  <h4 data-tour="analytics-year" style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }} data-tip="Card count and total value grouped by year — shows which eras make up your collection">By Year</h4>
                  <table className="analytics-table" style={{ width: "100%" }}>
                    <thead><tr>
                      <th onClick={() => toggleSort(setYearSort, "year")} style={{ cursor: "pointer", textAlign: "left" }}>Year{arrow(yearSort, "year")}</th>
                      <th onClick={() => toggleSort(setYearSort, "count")} style={{ cursor: "pointer" }}>Count{arrow(yearSort, "count")}</th>
                      <th onClick={() => toggleSort(setYearSort, "value")} style={{ cursor: "pointer" }}>Value{arrow(yearSort, "value")}</th>
                    </tr></thead>
                    <tbody>{sortData(stats.by_year, yearSort).map(row => (
                      <tr key={row.year ?? "__unknown__"}>
                        <td style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                          data-tip={`Click to filter Cards by ${row.year}`}
                          onClick={() => navigate("/list-cards", { state: { yearFilter: String(row.year), limit: "all" } })}>{row.year}</td>
                        <td>{row.count}</td><td>{fmtDollar(row.value)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>

                {/* By Player */}
                <div>
                  <h4 data-tour="analytics-player" style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }} data-tip="Card count and total value grouped by player — shows your most represented players">By Player</h4>
                  <table className="analytics-table" style={{ width: "100%" }}>
                    <thead><tr>
                      <th onClick={() => toggleSort(setPlayerSort, "name")} style={{ cursor: "pointer", textAlign: "left" }}>Player{arrow(playerSort, "name")}</th>
                      <th onClick={() => toggleSort(setPlayerSort, "count")} style={{ cursor: "pointer" }}>Count{arrow(playerSort, "count")}</th>
                      <th onClick={() => toggleSort(setPlayerSort, "value")} style={{ cursor: "pointer" }}>Value{arrow(playerSort, "value")}</th>
                    </tr></thead>
                    <tbody>{sortData(stats.by_player, playerSort).map((row, i) => (
                      <tr key={i}>
                        <td style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                          data-tip={`Click to filter Cards by ${row.name}`}
                          onClick={() => navigate("/list-cards", { state: { lastNameFilter: row.name.split(" ").slice(-1)[0], limit: "all" } })}>{row.name}</td>
                        <td>{row.count}</td><td>{fmtDollar(row.value)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Auto Balls ─────────────────────────────────────────────────────── */}
        <InventorySection id="balls" label="⚾ Auto Balls" data={stats.balls}
          open={openSections.has("balls")} onToggle={toggleSection} color="#d97706"
          breakdowns={[{ key: "by_signer", header: "By Signer" }, { key: "by_brand", header: "By Brand" }, { key: "by_commissioner", header: "By Commissioner" }]} />

        {/* ── Wax Boxes ──────────────────────────────────────────────────────── */}
        <InventorySection id="wax_boxes" label="📦 Wax Boxes" data={stats.wax_boxes}
          open={openSections.has("wax_boxes")} onToggle={toggleSection} color="#1d4ed8"
          breakdowns={[{ key: "by_brand", header: "By Brand" }, { key: "by_year", header: "By Year" }, { key: "by_type", header: "By Box Type" }]} />

        {/* ── Wax Packs ──────────────────────────────────────────────────────── */}
        <InventorySection id="wax_packs" label="🧧 Wax Packs" data={stats.wax_packs}
          open={openSections.has("wax_packs")} onToggle={toggleSection} color="#7c3aed"
          breakdowns={[{ key: "by_brand", header: "By Brand" }, { key: "by_year", header: "By Year" }, { key: "by_type", header: "By Pack Type" }]} />

        {/* ── Sets/Binders ───────────────────────────────────────────────────── */}
        <InventorySection id="set_binders" label="📓 Sets/Binders" data={stats.set_binders}
          open={openSections.has("set_binders")} onToggle={toggleSection} color="#16a34a"
          breakdowns={[{ key: "by_brand", header: "By Brand" }, { key: "by_year", header: "By Year" }, { key: "by_type", header: "By Set Type" }]} />

      </div>

      {showReport && (
        <div
          className="modal-overlay"
          role="button"
          tabIndex={0}
          onClick={() => setShowReport(false)}
          onKeyDown={e => { if (e.key === "Escape") setShowReport(false); }}
        >
          <div
            className="modal-box"
            style={{ width: 340, maxWidth: "95vw" }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: "1rem" }}>📄 Generate Report</h3>

            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem" }}>Include in report</div>
              {[
                ["Cards",         reportIncCards, setReportIncCards],
                ["Auto Balls",    reportIncBalls, setReportIncBalls],
                ["Wax Boxes",     reportIncWax,   setReportIncWax],
                ["Wax Packs",     reportIncPacks, setReportIncPacks],
                ["Sets / Binders",reportIncSets,  setReportIncSets],
              ].map(([label, checked, setter]) => (
                <label key={label} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.875rem", marginBottom: "0.3rem" }}>
                  <input type="checkbox" checked={checked} onChange={e => setter(e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>

            {reportIncCards && <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.75rem", marginBottom: "0.75rem" }}>
              <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem" }}>Cards options</div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "0.35rem", fontSize: "0.85rem" }}>
                Sort by
              </label>
              <select
                value={reportSort}
                onChange={e => setReportSort(e.target.value)}
                style={{ width: "100%", padding: "0.4rem 0.5rem", borderRadius: 6, border: "1px solid var(--border)" }}
              >
                <option value="last_name">Last Name</option>
                <option value="year">Year</option>
                <option value="brand">Brand</option>
                <option value="value">Value (high to low)</option>
              </select>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "0.35rem", fontSize: "0.85rem" }}>
                Include
              </label>
              <select
                value={reportScope}
                onChange={e => setReportScope(e.target.value)}
                style={{ width: "100%", padding: "0.4rem 0.5rem", borderRadius: 6, border: "1px solid var(--border)" }}
              >
                <option value="all">All cards</option>
                <option value="graded">Graded cards only</option>
                <option value="raw">Raw cards only</option>
              </select>
            </div>

            <div style={{ marginBottom: "0.5rem" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.875rem" }}>
                <input type="checkbox" checked={reportValued} onChange={e => setReportValued(e.target.checked)} />
                Only include cards with a value
              </label>
            </div>
            </div>}

            {reportError && (
              <p style={{ color: "#c62828", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{reportError}</p>
            )}

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowReport(false)}
                style={{ padding: "0.4rem 1rem", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={reportLoading}
                style={{
                  padding: "0.4rem 1.1rem", borderRadius: 6, border: "1px solid #e0a800",
                  background: reportLoading ? "#e0c040" : "#ffc107",
                  color: "#222", fontWeight: 600, cursor: reportLoading ? "not-allowed" : "pointer",
                }}
              >
                {reportLoading ? "Generating…" : "Download PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
